import React from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

/**
 * User table component with sorting, pagination, and selection
 */
const UserTable = ({
  users,
  loading,
  selectedUser,
  onUserSelect,
  pagination,
  onPageChange,
  onSort
}) => {
  const { theme } = useTheme();
  /**
   * Handle sort column click
   */
  const handleSort = (column) => {
    const currentSort = column;
    const newOrder = currentSort === column && pagination.sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(column, newOrder);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return null; // Return null so we can handle it in the component
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return null;
    }
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'active':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
            : 'bg-green-100 text-green-800 border border-green-200'
        }`;
      case 'inactive':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-red-900/30 text-red-400 border border-red-500/30' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`;
      case 'suspended':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30' 
            : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        }`;
      default:
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-gray-900/30 text-gray-400 border border-gray-500/30' 
            : 'bg-gray-100 text-gray-800 border border-gray-200'
        }`;
    }
  };

  /**
   * Get subscription badge styling
   */
  const getSubscriptionBadge = (planName) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    // Handle actual plan names from database: Starter, Growth, Professional, Enterprise
    const normalizedPlan = planName?.toLowerCase() || 'free';
    
    switch (normalizedPlan) {
      case 'starter':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-teal-900/30 text-teal-400 border border-teal-500/30' 
            : 'bg-teal-100 text-teal-800 border border-teal-200'
        }`;
      case 'growth':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
            : 'bg-green-100 text-green-800 border border-green-200'
        }`;
      case 'professional':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-purple-900/30 text-purple-400 border border-purple-500/30' 
            : 'bg-purple-100 text-purple-800 border border-purple-200'
        }`;
      case 'enterprise':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30' 
            : 'bg-blue-100 text-blue-800 border border-blue-200'
        }`;
      default:
        // Free or no plan
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-gray-900/30 text-gray-400 border border-gray-500/30' 
            : 'bg-gray-100 text-gray-800 border border-gray-200'
        }`;
    }
  };

  /**
   * Render sort icon
   */
  const SortIcon = ({ column, currentSort, sortOrder }) => {
    if (currentSort !== column) {
      return <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} ml-1`}>↕️</span>;
    }
    return (
      <span className={`${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'} ml-1`}>
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  /**
   * Render pagination controls
   */
  const PaginationControls = () => {
    const { page, totalPages } = pagination;
    const pages = [];
    
    // Calculate page range to show
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, page + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className={`flex items-center justify-between px-4 py-3 ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
      } border-t`}>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark' 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Previous
          </button>
          
          {startPage > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                1
              </button>
              {startPage > 2 && <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>...</span>}
            </>
          )}
          
          {pages.map(pageNum => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                pageNum === page
                  ? 'bg-teal-600 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {pageNum}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>...</span>}
              <button
                onClick={() => onPageChange(totalPages)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {totalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark' 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Next
          </button>
        </div>
        
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Page {page} of {totalPages}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`} data-theme={theme}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto"></div>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-4`}>Loading users...</p>
        </div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`} data-theme={theme}>
        <div className="p-8 text-center">
          <div className="text-4xl mb-4">👥</div>
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>No users found</h3>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            No users match your current filters. Try adjusting your search criteria.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden shadow-sm`} data-theme={theme}>
      {/* Table header */}
      <div className={`px-4 py-3 ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Users ({pagination.total})</h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className={`w-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
          <thead className={`${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
            <tr>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                } uppercase tracking-wider cursor-pointer transition-colors`}
                onClick={() => handleSort('first_name')}
              >
                Name
                <SortIcon column="first_name" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                } uppercase tracking-wider cursor-pointer transition-colors`}
                onClick={() => handleSort('email')}
              >
                Email
                <SortIcon column="email" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } uppercase tracking-wider`}>
                Status
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } uppercase tracking-wider`}>
                Subscription
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                } uppercase tracking-wider cursor-pointer transition-colors`}
                onClick={() => handleSort('created_at')}
              >
                Created
                <SortIcon column="created_at" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                } uppercase tracking-wider cursor-pointer transition-colors`}
                onClick={() => handleSort('last_login')}
              >
                Last Login
                <SortIcon column="last_login" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
            </tr>
          </thead>
          <tbody className={`${theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'} divide-y`}>
            {users.map((user) => (
              <tr
                key={user.id}
                className={`cursor-pointer transition-colors ${
                  selectedUser?.id === user.id
                    ? theme === 'dark'
                      ? 'bg-teal-900/20 border-l-4 border-l-teal-400'
                      : 'bg-teal-50 border-l-4 border-l-teal-500'
                    : theme === 'dark'
                      ? 'hover:bg-gray-700/50'
                      : 'hover:bg-gray-50'
                }`}
                onClick={() => onUserSelect(user)}
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className="h-8 w-8 rounded-full bg-teal-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white dark:text-white">
                          {(user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}`
                          : user.email
                        }
                      </div>
                      {user.first_name && user.last_name && (
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{user.email}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{user.email}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={getStatusBadge(user.status || 'active')}>
                    {user.status || 'Active'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={getSubscriptionBadge(user.plan_name || 'free')}>
                    {user.plan_name ? user.plan_name.charAt(0).toUpperCase() + user.plan_name.slice(1) : 'Free'}
                  </span>
                </td>
                <td className={`px-4 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatDate(user.created_at) || 'Unknown'}
                </td>
                <td className={`px-4 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatDate(user.last_login) || 'Never'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && <PaginationControls />}
    </div>
  );
};

export default UserTable;
